import React from 'react';
import { Link } from 'react-router';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import { C, container, sectionSub, primaryBtnStyle, ghostBtnStyle, CALENDLY_URL } from './publicStyles';
import usePageMeta from './usePageMeta';
import { PILOT_PATH, PILOT_GUARANTEE } from './publicContent';

// Self-serve Researcher plan ($500/seat) taken off sale 2026-10-29 (Peter). Billing code
// stays in place for any existing subscriber; nothing public sells it any more.

const PILOT_FEATURES = [
  'Three months on the full platform: generate, rank, analyze, validate, report',
  'A target from your own live program',
  'Success test agreed in writing before day one',
  'Hosted on our managed GPUs, nothing to install',
  'A workspace for your team, results saved to a searchable library',
];

const ENTERPRISE_FEATURES = [
  'Custom and fine-tuned models on your data',
  'Unpooled data with residency options',
  'Run in your own cloud (BYOC)',
  'Dedicated compute',
  'Priority support and onboarding',
];

function FeatureList({ items }) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '20px 0 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map(f => (
        <li key={f} style={{ color: C.body, fontSize: 14, lineHeight: 1.5, display: 'flex', gap: 10 }}>
          <span aria-hidden="true" style={{ color: C.green, flexShrink: 0 }}>✓</span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

const cardStyle = { background: C.panel, borderRadius: 14, padding: '32px 30px', display: 'flex', flexDirection: 'column' };

export default function PricingPage() {
  usePageMeta({
    title: 'Pricing: Pilots and Enterprise | CrimsonScan Spectra',
    description: 'Every Spectra engagement starts with a 3-month paid pilot on your own R&D program, with a success test agreed in writing. Then enterprise plans with custom models and BYOC.',
    path: '/pricing',
  });
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column' }}>
      <PublicNav />

      <main>
        <header style={{ ...container, textAlign: 'center', padding: '64px 24px 40px' }}>
          <h1 style={{ color: C.text, fontWeight: 800, fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', marginBottom: 14 }}>
            Start with a pilot on your own program
          </h1>
          <p style={{ ...sectionSub, margin: '0 auto' }}>
            Spectra is built for industrial R&D teams. Every engagement starts with a three-month
            paid pilot, hosted by us, then continues on an enterprise plan.
          </p>
        </header>

        <section style={{ ...container, paddingBottom: 40 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 22, maxWidth: 860, margin: '0 auto' }}>

            {/* Pilot */}
            <div style={{ ...cardStyle, border: `1px solid ${C.crimson}` }}>
              <p style={{ color: C.crimson, fontWeight: 700, fontSize: 12, letterSpacing: 2, margin: '0 0 8px' }}>STEP 1</p>
              <h2 style={{ color: C.text, fontWeight: 700, fontSize: '1.4rem', marginBottom: 4 }}>Paid pilot</h2>
              <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>Three months, fixed price, quoted up front</p>
              <p style={{ color: C.green, fontSize: 13, marginTop: 10, marginBottom: 0, lineHeight: 1.55 }}>{PILOT_GUARANTEE}</p>
              <FeatureList items={PILOT_FEATURES} />
              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Link to={PILOT_PATH} style={{ ...primaryBtnStyle, display: 'block', textAlign: 'center' }}>Request a pilot</Link>
                <p style={{ color: C.faint, fontSize: 12, textAlign: 'center', margin: 0 }}>
                  We reply within one business day with pilot terms.
                </p>
              </div>
            </div>

            {/* Enterprise */}
            <div style={{ ...cardStyle, border: `1px solid ${C.border}` }}>
              <p style={{ color: C.muted, fontWeight: 700, fontSize: 12, letterSpacing: 2, margin: '0 0 8px' }}>STEP 2</p>
              <h2 style={{ color: C.text, fontWeight: 700, fontSize: '1.4rem', marginBottom: 4 }}>Enterprise</h2>
              <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>Custom pricing, tailored to your deployment</p>
              <p style={{ color: C.muted, fontSize: 13, margin: '20px 0 0' }}>Everything in the pilot, plus:</p>
              <FeatureList items={ENTERPRISE_FEATURES} />
              <div style={{ marginTop: 'auto' }}>
                <a href={CALENDLY_URL} target="_blank" rel="noreferrer" style={{ ...ghostBtnStyle, display: 'block', textAlign: 'center' }}>
                  Book a demo
                </a>
              </div>
            </div>

          </div>
        </section>

        <section style={{ ...container, textAlign: 'center', paddingBottom: 72 }}>
          <p style={{ color: C.muted, fontSize: 14 }}>
            Want to see results first?{' '}
            <a href="/sample-report.pdf" target="_blank" rel="noreferrer" style={{ color: C.info, textDecoration: 'none' }}>Read a sample report</a>
            {' '}or the{' '}
            <Link to="/docs/benchmarks" style={{ color: C.info, textDecoration: 'none' }}>model benchmarks</Link>.
          </p>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
